import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { IntervalString } from '../../../clients/quipli-service/pricing/pricing';
import { SelectForm } from '../../../components/select-form/select-form';
import { Typography } from '../../../components/typography/typography';
import '../../../components/date-picker/date-picker.scss';

export const CollapsibleDeadDay = ({
  title,
  hours,
  setHours,
  reset,
}: {
  title: string;
  hours: IntervalString | undefined;
  setHours: (hours?: IntervalString) => void;
  reset: boolean;
}) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<string>(
    !hours ? 'free' : hours.start === '00:00' && hours.end === '23:59:59.99999' ? 'standard' : 'interval',
  );
  const [startTime, setStartTime] = useState<{ label: string; value: string }>(
    hours && hours.start !== '00:00'
      ? { value: hours.start, label: DateTime.fromISO(`2023-01-01T${hours.start}`).toFormat('h:mm a') }
      : { label: '9:00 AM', value: '09:00' },
  );
  const [endTime, setEndTime] = useState<{ label: string; value: string }>(
    hours && hours.end !== '23:59:59.99999'
      ? { value: hours.end, label: DateTime.fromISO(`2023-01-01T${hours.end}`).toFormat('h:mm a') }
      : { label: '5:00 PM', value: '17:00' },
  );
  const [startTimeChecked, setStartTimeChecked] = useState<boolean>(!!hours && hours.start !== '00:00');
  const [endTimeChecked, setEndTimeChecked] = useState<boolean>(!!hours && hours.end !== '23:59:59.99999');
  const [timeOptions /*setTimeOptions*/] = useState<{ label: string; value: string }[]>(
    (() => {
      let options: { label: string; value: string }[] = [
        { label: '12:00 AM', value: '00:00' },
        { label: '12:30 AM', value: '00:30' },
      ];
      for (let i = 1; i < 24; i++) {
        options = options.concat(
          { label: `${i < 13 ? i : i - 12}:00 ${i < 12 ? 'AM' : 'PM'}`, value: `${i < 10 ? '0' : ''}${i}:00` },
          { label: `${i < 13 ? i : i - 12}:30 ${i < 12 ? 'AM' : 'PM'}`, value: `${i < 10 ? '0' : ''}${i}:30` },
        );
        //TODO: not such a crap time picker
      }
      return options;
    })(),
  );

  const collapsedTitle = `(${
    !hours
      ? 'Always free when rental duration is less than 1 week'
      : hours.start === '00:00' && hours.end === '23:59:59.99999'
      ? 'Standard rate applies'
      : `${
          hours.end !== '23:59:59.99999'
            ? `Free if picked up after ${endTime.label}${hours.start !== '00:00' ? ' and f' : ''}`
            : 'F'
        }${hours.start !== '00:00' ? `ree if returned before ${startTime.label}` : ''}`
  })`;

  useEffect(() => {
    setSelectedOption(
      !hours ? 'free' : hours.start === '00:00' && hours.end === '23:59:59.99999' ? 'standard' : 'interval',
    );
    setStartTime(
      hours && hours.start !== '00:00'
        ? { value: hours.start, label: DateTime.fromISO(`2023-01-01T${hours.start}`).toFormat('h:mm a') }
        : { label: '9:00 AM', value: '09:00' },
    );
    setEndTime(
      hours && hours.end !== '23:59:59.99999'
        ? { value: hours.end, label: DateTime.fromISO(`2023-01-01T${hours.end}`).toFormat('h:mm a') }
        : { label: '5:00 PM', value: '17:00' },
    );
    setStartTimeChecked(!!hours && hours.start !== '00:00');
    setEndTimeChecked(!!hours && hours.end !== '23:59:59.99999');
    setIsVisible(false);
  }, [reset]);

  useEffect(() => {
    if (selectedOption === 'standard') {
      setHours({ start: '00:00', end: '23:59:59.99999' });
    } else if (selectedOption === 'free') {
      setHours(undefined);
    } else {
      setHours({
        start: startTimeChecked ? startTime.value : '00:00',
        end: endTimeChecked ? endTime.value : '23:59:59.99999',
      });
    }
  }, [selectedOption, startTime, endTime, startTimeChecked, endTimeChecked]);

  return (
    <div className="dead-day">
      <div className="border border-solid border-grey-300 py-2.5 px-3.5 rounded bg-white">
        <div
          onClick={() => setIsVisible(!isVisible)}
          className="flex justify-between hover:cursor-pointer items-center"
        >
          <div className="flex gap-1 items-baseline">
            <Typography type="text-md" weight="medium">
              {title}
            </Typography>
            {!isVisible && (
              <Typography type="text-sm" weight="regular" className="collapsed-title">
                {collapsedTitle}
              </Typography>
            )}
          </div>
          <span>
            <i className={`text-grey-500 fa ${isVisible ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
          </span>
        </div>

        {isVisible && (
          <div className="mt-1">
            <div className="my-4 flex gap-3 items-start">
              <button
                className={`radio-button ${selectedOption === 'standard' && 'selected'}`}
                onClick={() => setSelectedOption('standard')}
              >
                <span className="inner" />
              </button>
              <Typography className="text-grey-700" type="text-md">
                Standard rate applies
              </Typography>
            </div>
            <div className="mb-4 flex gap-3 items-start">
              <button
                className={`radio-button ${selectedOption === 'free' && 'selected'}`}
                onClick={() => setSelectedOption('free')}
              >
                <span className="inner" />
              </button>

              <Typography className="text-grey-700" type="text-md">
                Always free when rental duration is less than 1 week
              </Typography>
            </div>

            <div>
              <div className="mb-4 flex gap-3 items-start">
                <button
                  className={`radio-button ${selectedOption === 'interval' && 'selected'}`}
                  onClick={() => {
                    setSelectedOption('interval');
                    setStartTimeChecked(true);
                    setEndTimeChecked(true);
                  }}
                >
                  <span className="inner" />
                </button>
                <Typography className="text-grey-700" type="text-md">
                  Specify time period the product will be free of charge
                </Typography>
              </div>
              {selectedOption === 'interval' && (
                <div className="mt-2 grid grid-cols-2 gap-4">
                  <div className="m-1">
                    <div className="flex gap-2 mb-2">
                      <button
                        className={`checkbox ${endTimeChecked && 'selected'}`}
                        onClick={() => setEndTimeChecked(!endTimeChecked)}
                      >
                        <i className="fa fa-check" />
                      </button>
                      <Typography className="text-grey-700" type="text-md">
                        Pick up time
                      </Typography>
                    </div>
                    <SelectForm
                      options={timeOptions}
                      value={endTime}
                      onChange={(t) => {
                        if (t) setEndTime(t);
                      }}
                      isClearable={false}
                    />
                    <div className="mt-1.5">
                      <Typography className="text-grey-500" type="text-xs">
                        Free if picked up after this time
                      </Typography>
                    </div>
                  </div>

                  <div className="m-1">
                    <div className="flex gap-2 mb-2">
                      <button
                        className={`checkbox ${startTimeChecked && 'selected'}`}
                        onClick={() => setStartTimeChecked(!startTimeChecked)}
                      >
                        <i className="fa fa-check" />
                      </button>
                      <Typography className="text-grey-700" type="text-md">
                        Return time
                      </Typography>
                    </div>
                    <SelectForm
                      options={timeOptions}
                      value={startTime}
                      onChange={(t) => {
                        if (t) setStartTime(t);
                      }}
                      isClearable={false}
                    />
                    <div className="mt-1.5">
                      {' '}
                      <Typography className="text-grey-500 mt-1.5" type="text-xs">
                        Free if returned before this time
                      </Typography>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
